import React, { useState, useCallback, useRef, useEffect } from 'react';
import './actionToast.css';

// Shared action-confirmation toast. Small bordered card pinned to the
// bottom-right. Replaces the previously-inline toast pattern in
// BriefDetail and is the canonical home for admin-side affirmations
// ("yes, that worked") across the product.
//
// v2 of the toast: now supports stacking up to MAX_STACK toasts, an
// optional action button (e.g. Undo), and a countdown progress bar so
// the user has a visual hint of how long the action is reversible.
//
// Usage:
//   const { notify, Toast } = useActionToast();
//   notify({ eyebrow: 'Brief muted', title: 'Notifications paused', body: '…' });
//   notify(
//     { eyebrow: 'Archived', title: 'Brief filed', tone: 'archive',
//       action: { label: 'Undo', onClick: () => reverseArchive() } },
//     { holdMs: 6000 }
//   );
//   ...
//   <Toast />   // mount once anywhere in the page tree
//
// notify() options:
//   { syncWith }  timestamp in ms — delays the toast by max(0, 600 - elapsed)
//                 so a toast fired alongside a ~600ms paper-plane flight (or
//                 other choreographed sequence) lands as that animation
//                 completes rather than competing with it
//   { holdMs }    how long the toast stays visible (default 4000). For Undo
//                 flows we use 6000 — long enough to catch a misclick, short
//                 enough not to linger.
//
// Toast shape:
//   { eyebrow, title, body?, tone?, action? }
//   - tone: 'success' (default, green) | 'archive' (amber) | 'info' (white)
//   - action: { label, onClick } — renders an inline button alongside the X.
//             Calling onClick auto-dismisses the toast.
//
// Stack behaviour: up to MAX_STACK toasts visible (newest at the top of the
// column). A fourth toast pushes out the oldest. Each toast owns its own
// hold timer so they expire independently.

const MAX_STACK = 3;

let nextToastId = 1;
const allocateId = () => {
  const id = nextToastId;
  nextToastId += 1;
  return id;
};

export function useActionToast() {
  const [toasts, setToasts] = useState([]);
  const hideTimersRef = useRef(new Map());
  const showTimersRef = useRef(new Set());

  // Belt-and-braces cleanup so a page unmounting mid-toast doesn't try to
  // setState on a torn-down component.
  useEffect(() => {
    const showTimers = showTimersRef.current;
    const hideTimers = hideTimersRef.current;
    return () => {
      showTimers.forEach(t => clearTimeout(t));
      hideTimers.forEach(t => clearTimeout(t));
      showTimers.clear();
      hideTimers.clear();
    };
  }, []);

  const dismiss = useCallback((id) => {
    if (id == null) {
      // Dismiss everything currently visible.
      hideTimersRef.current.forEach(t => clearTimeout(t));
      hideTimersRef.current.clear();
      setToasts([]);
      return;
    }
    const t = hideTimersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      hideTimersRef.current.delete(id);
    }
    setToasts(prev => prev.filter(x => x.id !== id));
  }, []);

  const notify = useCallback((nextToast, opts = {}) => {
    if (!nextToast) return null;
    const { syncWith = null, holdMs = 4000 } = opts;
    const id = allocateId();
    const enriched = { id, holdMs, ...nextToast };
    const elapsed = syncWith ? Date.now() - syncWith : 0;
    const delay = Math.max(0, 600 - elapsed);
    const showTimer = setTimeout(() => {
      showTimersRef.current.delete(showTimer);
      setToasts(prev => {
        const next = [...prev, enriched];
        // Cap to MAX_STACK; oldest fall off the front first.
        return next.length > MAX_STACK ? next.slice(next.length - MAX_STACK) : next;
      });
      const hideTimer = setTimeout(() => {
        hideTimersRef.current.delete(id);
        setToasts(prev => prev.filter(x => x.id !== id));
      }, holdMs);
      hideTimersRef.current.set(id, hideTimer);
    }, delay);
    showTimersRef.current.add(showTimer);
    return id;
  }, []);

  // Render helper bound to this hook instance's state so the parent doesn't
  // have to wire `toasts` and `onDismiss` props by hand.
  const Toast = useCallback(
    () => <ActionToastStack toasts={toasts} onDismiss={dismiss} />,
    [toasts, dismiss],
  );

  // Back-compat: surface the most recent toast as `toast` for any callers
  // still reading the singular value. Stays in sync with the stack.
  const toast = toasts.length ? toasts[toasts.length - 1] : null;

  return { notify, dismiss, toast, toasts, Toast };
}

// Stack container — fixed at bottom-right, renders the live toast array
// in a column with a small gap. Each individual toast is an
// <ActionToastView /> bound to its own dismiss handler so action / X
// clicks remove only that toast.
export function ActionToastStack({ toasts, onDismiss }) {
  if (!toasts || toasts.length === 0) return null;
  return (
    <div
      className="fixed bottom-8 right-8 z-[100] flex flex-col gap-2.5 items-end pointer-events-none"
      aria-live="polite"
    >
      {toasts.map(t => (
        <ActionToastView
          key={t.id}
          toast={t}
          onDismiss={() => onDismiss(t.id)}
        />
      ))}
    </div>
  );
}

// Tone palette — drives accent strip, eyebrow colour, action button hue
// and progress-bar fill. 'success' is the historical green default.
const TONE_PALETTE = {
  success: {
    accent: '#92D400',
    accentSoft: 'rgba(146,212,0,0.4)',
    iconBg: 'rgba(146,212,0,0.15)',
    iconBorder: 'rgba(146,212,0,0.3)',
    iconStroke: '#92D400',
    progress: '#92D400',
  },
  archive: {
    accent: '#E89A2E',
    accentSoft: 'rgba(232,154,46,0.4)',
    iconBg: 'rgba(232,154,46,0.15)',
    iconBorder: 'rgba(232,154,46,0.3)',
    iconStroke: '#E89A2E',
    progress: '#E89A2E',
  },
  info: {
    accent: '#DADCCD',
    accentSoft: 'rgba(218,220,205,0.4)',
    iconBg: 'rgba(218,220,205,0.15)',
    iconBorder: 'rgba(218,220,205,0.3)',
    iconStroke: '#DADCCD',
    progress: '#DADCCD',
  },
  // Error/failure confirmations — so one toast mechanism covers success AND
  // failure and callers never fall back to an ad-hoc red toast.
  danger: {
    accent: '#E0290A',
    accentSoft: 'rgba(224,41,10,0.4)',
    iconBg: 'rgba(224,41,10,0.12)',
    iconBorder: 'rgba(224,41,10,0.3)',
    iconStroke: '#E0290A',
    progress: '#E0290A',
  },
};

// Standalone view if a caller wants to render the toast manually with
// externally-managed state. The hook's bound <Toast /> uses this via
// ActionToastStack for the common case.
export function ActionToastView({ toast, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  // Mount → next frame applies the visible class so the slide-in
  // animation runs from off-canvas → resting state.
  useEffect(() => {
    if (!toast) return undefined;
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [toast]);

  if (!toast) return null;

  const palette = TONE_PALETTE[toast.tone] || TONE_PALETTE.success;
  const showProgress = Number(toast.holdMs) >= 3000;

  const beginDismiss = (cb) => {
    setDismissing(true);
    // Match the CSS transition (~280ms) before unmounting via the
    // parent's onDismiss callback so the slide-out completes visibly.
    setTimeout(() => { if (cb) cb(); }, 280);
  };

  const handleActionClick = () => {
    if (typeof toast.action?.onClick === 'function') toast.action.onClick();
    beginDismiss(onDismiss);
  };

  const handleDismissClick = () => beginDismiss(onDismiss);

  return (
    <div
      role="status"
      className={`action-toast ${visible && !dismissing ? 'action-toast--visible' : ''} ${dismissing ? 'action-toast--dismissing' : ''} relative bg-white rounded-xl pl-5 pr-4 py-3 flex items-start gap-3 max-w-sm shadow-2xl shadow-black/50 pointer-events-auto overflow-hidden`}
      style={{
        borderLeft: `3px solid ${palette.accent}`,
        border: `1px solid ${palette.accentSoft}`,
        borderLeftWidth: '3px',
        borderLeftColor: palette.accent,
        minWidth: 320,
      }}
    >
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: palette.iconBg, border: `1px solid ${palette.iconBorder}` }}
      >
        {toast.tone === 'archive' ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={palette.iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 8v13H3V8" />
            <path d="M1 3h22v5H1z" />
            <line x1="10" y1="12" x2="14" y2="12" />
          </svg>
        ) : toast.tone === 'danger' ? (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={palette.iconStroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={palette.iconStroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>
      <div className="min-w-0 flex-1">
        {toast.eyebrow && (
          <p className="font-['Metropolis'] text-[10px] font-bold tracking-[2.5px] uppercase mb-1" style={{ color: palette.accent }}>
            {toast.eyebrow}
          </p>
        )}
        <p className="font-['Metropolis'] text-sm font-semibold text-ink leading-tight">{toast.title}</p>
        {toast.body && (
          <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mt-1">{toast.body}</p>
        )}
      </div>
      {toast.action && typeof toast.action.onClick === 'function' && (
        <button
          type="button"
          onClick={handleActionClick}
          className="font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.12em] px-2.5 py-1.5 rounded-md hover:bg-active-green/10 transition-colors flex-shrink-0"
          style={{ color: palette.accent }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={handleDismissClick}
        aria-label="Dismiss"
        className="font-['Metropolis'] text-[#51534A] hover:text-ink transition-colors text-sm flex-shrink-0 leading-none self-center"
      >
        ×
      </button>
      {showProgress && (
        <div
          className="action-toast__progress absolute bottom-0 left-0 h-[2px] w-full"
          style={{
            background: palette.progress,
            '--toast-hold': `${toast.holdMs}ms`,
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default ActionToastView;
